/**
 * Jenny's weekly ads report, laid out for the owner: her take (a few plain sentences and four
 * numbers), where the money went, the funnel, and what stood out. Everything here is computed from
 * the figures of the performance read the report cites, never from her text, and it keeps her
 * reporting rules: each platform's conversions are that platform's own count and are never added
 * across platforms, there is no blended cost-per-result, and a flag states a fact, never a fix.
 */

import type { AdsReportData, AdsReportPlatform, AdsReportTotals } from '../types';

export type AdsMetric = 'spend' | 'clicks' | 'conversions';

/** "Google", "Meta": who did the counting, for "Google's count". */
export function countedBy(platform: Pick<AdsReportPlatform, 'platform' | 'label'>): string {
  if (/google/i.test(platform.platform) || /google/i.test(platform.label)) return 'Google';
  if (/meta|facebook/i.test(platform.platform) || /meta/i.test(platform.label)) return 'Meta';
  return platform.label.replace(/\s+ads$/i, '');
}

export function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** A count as the platform gives it: Google's conversions can be fractional (77.5), and are never rounded to 78. */
export function count(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

export function whole(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function percent(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

function listNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

type ReadyPlatform = AdsReportPlatform & { totals: AdsReportTotals };

export function readyPlatforms(report: AdsReportData): ReadyPlatform[] {
  return report.platforms.filter(
    (p): p is ReadyPlatform => p.state === 'ready' && p.totals !== null,
  );
}

const NAME_SEPARATOR = /\s+[-–|:/]\s+/g;

/**
 * Campaign names with the prefix every one of them shares taken off ("Little Heroes - Anderson -
 * Branded" → "Branded"), cut only at a separator, so the page does not repeat the practice and
 * location on every line. Left as they are when there is one campaign or nothing is shared.
 */
export function withShortCampaignNames(report: AdsReportData): AdsReportData {
  const names = report.platforms.flatMap((p) => p.campaigns.map((c) => c.name));
  if (names.length < 2) return report;
  let shared = names[0];
  for (const name of names.slice(1)) {
    let i = 0;
    while (i < shared.length && i < name.length && shared[i] === name[i]) i += 1;
    shared = shared.slice(0, i);
  }
  let cut = 0;
  for (const match of shared.matchAll(NAME_SEPARATOR)) cut = (match.index ?? 0) + match[0].length;
  if (cut === 0 || names.some((name) => name.slice(cut).trim() === '')) return report;
  return {
    ...report,
    platforms: report.platforms.map((p) => ({
      ...p,
      campaigns: p.campaigns.map((c) => ({ ...c, name: c.name.slice(cut) })),
    })),
  };
}

export interface AdsCampaignView extends AdsReportTotals {
  key: string;
  name: string;
  platformLabel: string;
  countedBy: string;
  opaque: boolean;
}

/** Every campaign that spent or was shown, across ready platforms, biggest spend first. */
export function activeCampaigns(report: AdsReportData): AdsCampaignView[] {
  return readyPlatforms(report)
    .flatMap((p) =>
      p.campaigns
        .filter((c) => c.spend > 0 || c.impressions > 0)
        .map((c, i) => ({
          ...c,
          key: `${p.platform}:${i}:${c.name}`,
          platformLabel: p.label,
          countedBy: countedBy(p),
        })),
    )
    .sort((a, b) => b.spend - a.spend);
}

const RUNNING_STATUS = /^(enabled|active|running|on)$/i;

/** Campaigns switched on that nobody saw: no spend and no impressions in the window. */
export function idleCampaigns(report: AdsReportData): string[] {
  return readyPlatforms(report).flatMap((p) =>
    p.campaigns
      .filter((c) => c.spend === 0 && c.impressions === 0 && RUNNING_STATUS.test(c.status ?? ''))
      .map((c) => c.name),
  );
}

export interface AdsTake {
  sentences: string;
  tiles: Array<{ label: string; value: string; note: string }>;
}

/**
 * Jenny's take, or null when no platform has figures (the report then shows as text only).
 * Spend and impressions add up honestly across platforms; conversions are shown per platform.
 */
export function adsTake(report: AdsReportData): AdsTake | null {
  const ready = readyPlatforms(report);
  if (ready.length === 0) return null;
  const spending = ready.filter((p) => p.totals.spend > 0);
  const spend = ready.reduce((n, p) => n + p.totals.spend, 0);
  const impressions = ready.reduce((n, p) => n + p.totals.impressions, 0);
  const clicks = ready.reduce((n, p) => n + p.totals.clicks, 0);
  const days = report.days;

  const parts: string[] = [];
  if (spending.length === 0) {
    parts.push(`Your ads spent nothing in the last ${days} days.`);
  } else if (spending.length === 1) {
    const p = spending[0];
    parts.push(
      `Your ${p.label} spent ${money(p.totals.spend)} in the last ${days} days and ${countedBy(p)} counted ${count(p.totals.conversions)} conversions.`,
    );
  } else {
    parts.push(
      `Your ads spent ${money(spend)} in the last ${days} days: ${listNames(spending.map((p) => `${p.label} ${money(p.totals.spend)}`))}.`,
    );
    parts.push(
      `${listNames(spending.map((p) => `${countedBy(p)} counted ${count(p.totals.conversions)} conversions`))}.`,
    );
  }
  const campaigns = activeCampaigns(report);
  const top = campaigns[0];
  if (top && campaigns.length > 1 && spend > 0) {
    const share = percent(top.spend, spend);
    parts.push(
      share >= 50
        ? `Most of the money, ${share}%, went to one campaign: ${top.name}.`
        : `The biggest share, ${share}%, went to ${top.name}.`,
    );
  }
  for (const p of ready) {
    if (p.totals.spend === 0 && spending.length > 0)
      parts.push(`${countedBy(p)} ran nothing this period.`);
  }
  for (const p of report.platforms) {
    if (p.state !== 'ready')
      parts.push(
        `${p.label} isn't in this report${p.reason ? ` (${p.reason.replace(/\.$/, '').toLowerCase()})` : ''}.`,
      );
  }

  const spendNote =
    ready.length > 1
      ? ready.map((p) => `${countedBy(p)} ${money(p.totals.spend)}`).join(' · ')
      : `last ${days} days`;
  const conversionsTile =
    spending.length <= 1
      ? (() => {
          const p = spending[0] ?? ready[0];
          const each =
            p.totals.conversions > 0
              ? `${money(p.totals.spend / p.totals.conversions)} each, ${countedBy(p)}'s figure`
              : `${countedBy(p)}'s count`;
          return {
            label: `Conversions, as ${countedBy(p)} counts them`,
            value: count(p.totals.conversions),
            note: each,
          };
        })()
      : {
          label: 'Conversions, each platform’s own count',
          value: spending.map((p) => count(p.totals.conversions)).join(' · '),
          note: `${spending.map((p) => countedBy(p)).join(' · ')}, not added together`,
        };
  return {
    sentences: parts.join(' '),
    tiles: [
      { label: 'Spent', value: money(spend), note: spendNote },
      { label: 'Times your ads were shown', value: whole(impressions), note: 'impressions' },
      {
        label: 'Clicks',
        value: whole(clicks),
        note:
          impressions > 0
            ? `${((clicks / impressions) * 100).toFixed(1)}% of times shown`
            : 'no impressions',
      },
      conversionsTile,
    ],
  };
}

/**
 * The headline tiles of a report with calls: spent, calls of 30s or more, what each cost, missed. Null when the
 * report has no call figures (the tiles then stay spend, reach and the platform's conversions).
 */
export function callTiles(figures: {
  spend: number | null;
  costPerCallAtLeast30s: number | null;
  calls: number | null;
  atLeast30s: number | null;
  missed: number | null;
}): Array<{ label: string; value: string; note: string }> | null {
  const { spend, costPerCallAtLeast30s, calls, atLeast30s, missed } = figures;
  if (spend === null || calls === null || atLeast30s === null) return null;
  return [
    { label: 'Spent', value: money(spend), note: 'last 30 days' },
    {
      label: 'Calls of 30s or more',
      value: whole(atLeast30s),
      note: `of ${whole(calls)} calls from ads`,
    },
    {
      label: 'Cost per 30s+ call',
      value: costPerCallAtLeast30s === null ? 'n/a' : money(costPerCallAtLeast30s),
      note: 'Google Ads spend ÷ calls of 30s or more',
    },
    {
      label: 'Missed calls',
      value: missed === null ? 'n/a' : whole(missed),
      note: `of ${whole(calls)} calls`,
    },
  ];
}

export interface AdsBar {
  key: string;
  name: string;
  detail: string;
  value: string;
  share: string;
  /** 0-100, relative to the largest campaign on this measure. */
  width: number;
}

/** One bar per campaign for the chosen measure. */
export function campaignBars(report: AdsReportData, metric: AdsMetric): AdsBar[] {
  const campaigns = activeCampaigns(report);
  const multiPlatform = new Set(campaigns.map((c) => c.platformLabel)).size > 1;
  const value = (c: AdsCampaignView) => c[metric];
  const max = Math.max(0, ...campaigns.map(value));
  return campaigns.map((c) => {
    // Conversions are a share of that platform's own count, never of a cross-platform sum.
    const total = campaigns
      .filter((x) => metric !== 'conversions' || x.platformLabel === c.platformLabel)
      .reduce((n, x) => n + value(x), 0);
    return {
      key: c.key,
      name: c.name,
      detail: [multiPlatform ? c.platformLabel : null, c.opaque ? 'automated, little detail' : null]
        .filter(Boolean)
        .join(' · '),
      value: metric === 'spend' ? money(c.spend) : whole(value(c)),
      share:
        total > 0
          ? `${percent(value(c), total)}% of ${metric === 'conversions' ? `${c.countedBy}'s count` : 'total'}`
          : '',
      // Zero is no bar at all, never a sliver that reads as a little.
      width: max > 0 && value(c) > 0 ? Math.max(2, Math.round((value(c) / max) * 100)) : 0,
    };
  });
}

export interface AdsFunnelStep {
  label: string;
  note: string;
  value: string;
  /** 0-100, relative to the first step. */
  width: number;
}

/** Shown → clicked → calls (Google, when its read has them), or the platform's own conversions when it
 *  has no calls; the page then shows where the ad data stops. Never both: conversions and calls are
 *  different counts, and a conversions step above calls read as if one led to the other. */
export function funnelFor(platform: ReadyPlatform): AdsFunnelStep[] {
  const t = platform.totals;
  const base = Math.max(t.impressions, 1);
  // Bars on a square-root scale: on a straight scale clicks (a few percent of impressions) would be
  // a sliver, and the eye would read "nothing happened".
  const width = (n: number) => (n <= 0 ? 0 : Math.max(4, Math.round(Math.sqrt(n / base) * 100)));
  const by = countedBy(platform);
  const calls = platform.calls;
  const phoneConversions = calls?.phoneCallConversions;
  const steps: AdsFunnelStep[] = [
    {
      label: 'Shown',
      note: 'times an ad appeared',
      value: whole(t.impressions),
      width: t.impressions > 0 ? 100 : 0,
    },
    {
      label: 'Clicked',
      note:
        t.impressions > 0
          ? `${((t.clicks / t.impressions) * 100).toFixed(1)}% of times shown`
          : 'no impressions',
      value: whole(t.clicks),
      width: width(t.clicks),
    },
  ];
  if (!calls || calls.reportingEnabled === false) {
    steps.push({
      label: 'Conversions',
      note:
        phoneConversions != null && t.conversions > 0
          ? `${by}'s own count · ${by} also counts ${count(phoneConversions)} phone-call conversions`
          : `${by}'s own count`,
      value: count(t.conversions),
      width: width(t.conversions),
    });
  }
  if (calls?.reportingEnabled === false) {
    steps.push({
      label: 'Calls',
      note: `call reporting is off in ${by}, so calls aren't recorded`,
      value: '—',
      width: 0,
    });
  } else if (calls) {
    steps.push({
      label: 'Calls',
      note: `${whole(calls.answered)} answered`,
      value: whole(calls.total),
      width: width(calls.total),
    });
    steps.push({
      label: 'Calls of 30s or more',
      note:
        calls.total > 0
          ? `${Math.round((calls.atLeast30s / calls.total) * 100)}% of calls`
          : 'no calls',
      value: whole(calls.atLeast30s),
      width: width(calls.atLeast30s),
    });
  }
  return steps;
}

/** The last, greyed row of the funnel: what the ad data cannot see once calls are shown. */
export function funnelEndLabel(platform: ReadyPlatform): string {
  return platform.calls && platform.calls.reportingEnabled !== false
    ? 'Booked patients'
    : 'Answered calls, booked patients';
}

export interface AdsFlag {
  title: string;
  body: string;
  /** What "Ask Jenny about this" sends. */
  question: string;
}

const MAX_FLAGS = 4;

/** What stood out: facts worth a look, never what to do about them (that is the strategy review's). */
export function adsFlags(report: AdsReportData): AdsFlag[] {
  const ready = readyPlatforms(report);
  const spend = ready.reduce((n, p) => n + p.totals.spend, 0);
  const campaigns = activeCampaigns(report);
  const flags: AdsFlag[] = [];

  const top = campaigns[0];
  if (top && campaigns.length > 1 && spend > 0 && percent(top.spend, spend) >= 50) {
    const share = percent(top.spend, spend);
    const platform = ready.find((p) => p.label === top.platformLabel);
    flags.push({
      title: `${share}% of the spend went to one campaign`,
      body: `${top.name} spent ${money(top.spend)} of ${money(spend)}${platform ? ` and brought ${count(top.conversions)} of the ${count(platform.totals.conversions)} ${top.countedBy}-counted conversions` : ''}.`,
      question: `Why did ${top.name} get ${share}% of the spend?`,
    });
  }

  const opaqueSpend = campaigns.filter((c) => c.opaque).reduce((n, c) => n + c.spend, 0);
  if (spend > 0 && percent(opaqueSpend, spend) >= 50) {
    const names = campaigns.filter((c) => c.opaque).map((c) => c.name);
    flags.push({
      title: `${percent(opaqueSpend, spend)}% of the spend went to automated campaigns`,
      body: `${listNames(names)} ${names.length === 1 ? 'is an automated campaign' : 'are automated campaigns'}: the platform doesn't show which searches or calls ${names.length === 1 ? 'its' : 'their'} results came from.`,
      question: 'What can we see, and not see, about the automated campaigns?',
    });
  }

  for (const c of campaigns
    .filter((x) => x.conversions === 0 && x.spend > 0 && percent(x.spend, spend) >= 5)
    .slice(0, 2)) {
    flags.push({
      title: `${c.name} spent ${money(c.spend)} with no conversions`,
      body: `${c.countedBy} counted no conversions from it in the last ${report.days} days.`,
      question: `Why did ${c.name} get no conversions?`,
    });
  }

  const idle = idleCampaigns(report);
  if (idle.length > 0) {
    flags.push({
      title:
        idle.length === 1
          ? 'A campaign is on but didn’t run'
          : `${idle.length} campaigns are on but didn’t run`,
      body: `${listNames(idle)} ${idle.length === 1 ? 'is' : 'are'} switched on, but nobody was shown ${idle.length === 1 ? 'it' : 'them'}: no spend and no impressions.`,
      question: `Why ${idle.length === 1 ? "wasn't" : "weren't"} ${listNames(idle)} shown to anyone?`,
    });
  }

  for (const p of ready) {
    const calls = p.calls;
    // A quarter or more of the calls from ads went unanswered: worth a look, not a verdict.
    if (
      calls &&
      calls.reportingEnabled !== false &&
      calls.total >= 4 &&
      calls.total - calls.answered >= calls.total / 4
    ) {
      const missed = calls.total - calls.answered;
      flags.push({
        title: `${whole(missed)} of ${whole(calls.total)} calls from ads went unanswered`,
        body: `${countedBy(p)} recorded ${whole(calls.total)} calls from your ads in the last ${report.days} days; ${whole(calls.answered)} were answered.`,
        question: 'When did the unanswered calls from ads come in?',
      });
    }
  }

  for (const p of ready) {
    if (p.totals.spend === 0 && spend > 0) {
      flags.push({
        title: `${countedBy(p)} spent nothing`,
        body: `No ${countedBy(p)} campaign spent anything in the last ${report.days} days, so every figure here is from ${listNames(ready.filter((x) => x.totals.spend > 0).map((x) => x.label))}.`,
        question: `Is ${countedBy(p)} supposed to be running for us?`,
      });
    }
  }
  return flags.slice(0, MAX_FLAGS);
}
